import { GridColDef } from '@mui/x-data-grid-pro'
import { PillProps } from 'ext-ui/components/Pill/Pill'
import React from 'react'
import AssignCardholderDropdown, {
  CardholderSelection,
} from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import CardActionsMenu from 'src/app/CardFeedDetails/components/CardActionsMenu'
import CardFeedCardholderCell from 'src/app/CardFeedDetails/components/CardFeedCardholderCell'
import * as style from 'src/app/CardFeedDetails/components/CardFeedCardsTable.style'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import MaskedCardNumber from 'src/components/MaskedCardNumber/MaskedCardNumber'
import { dateFormatString } from 'src/localization/Dte'
import {
  CardFeedCard,
  CardFeedCardStatus,
  isInviteSentCardFeedCard,
} from 'src/queries/cardFeeds/cardFeeds.models'

const STATUS_LABEL: Record<CardFeedCardStatus, string> = {
  ASSIGNED: 'Assigned',
  UNASSIGNED: 'Unassigned',
  PENDING: 'Pending',
  // The bank closing a card surfaces as FAILED on the backend; we label it
  // "Inactive" in the UI to match what that state means to the user (EX-39827).
  FAILED: 'Inactive',
}

const STATUS_PILL_COLOR: Record<CardFeedCardStatus, PillProps['color']> = {
  ASSIGNED: 'highlight',
  UNASSIGNED: 'pending',
  PENDING: undefined,
  // "Inactive" (a bank-closed card) is a neutral end state, not an error, so it
  // uses the default gray pill rather than the red severe color (EX-39827).
  FAILED: undefined,
}

const getStatusLabel = (card: CardFeedCard): string =>
  isInviteSentCardFeedCard(card) ? 'Invite Sent' : STATUS_LABEL[card.status]

export interface BuildCardFeedColumnsArgs {
  translate: (str: string, expressions?: Record<string, string>) => string
  onAssignFromRow: (card: CardFeedCard, selection: CardholderSelection) => void
  onReinvite: (card: CardFeedCard) => void
  onUnassign: (card: CardFeedCard) => void
  // Only a connected (ACTIVE) feed can take assignments. When false, the Card
  // Holder cell shows a static placeholder for unassigned cards instead of the
  // interactive assign dropdown.
  isActive: boolean
}

export const buildCardFeedColumns = ({
  translate,
  onAssignFromRow,
  onReinvite,
  onUnassign,
  isActive,
}: BuildCardFeedColumnsArgs): GridColDef<CardFeedCard>[] => [
  {
    field: 'cardName',
    headerName: translate('Card Display Name'),
    sortable: true,
    flex: 1,
    minWidth: 160,
    renderCell: (params): React.ReactElement => (
      <DataGridCell text={params.row.cardName} bold params={params} />
    ),
  },
  {
    field: 'cardNumber',
    headerName: translate('Card Number'),
    sortable: false,
    flex: 1,
    minWidth: 150,
    renderCell: (params): React.ReactElement => (
      <DataGridCell
        text={
          <MaskedCardNumber
            lastFour={params.row.lastFour}
            css={style.cardNumber}
          />
        }
        params={params}
      />
    ),
  },
  {
    field: 'cardHolder',
    headerName: translate('Card Holder'),
    sortable: true,
    flex: 1,
    minWidth: 180,
    renderCell: (params): React.ReactElement =>
      params.row.assignedUser ? (
        <CardFeedCardholderCell
          assignedUser={params.row.assignedUser}
          params={params}
        />
      ) : // Only an UNASSIGNED row gets the inline assign dropdown. An ASSIGNED,
      // PENDING, or FAILED row whose assignee the backend couldn't resolve
      // (assignedUserId present, assignedUser omitted) is already owned — it
      // must not offer re-assignment, so it falls through to the read-only
      // placeholder.
      isActive && params.row.status === 'UNASSIGNED' ? (
        <AssignCardholderDropdown
          portalToBody
          onSelect={(selection): void => onAssignFromRow(params.row, selection)}
        />
      ) : (
        <DataGridCell text="—" params={params} />
      ),
  },
  {
    field: 'status',
    headerName: translate('Status'),
    sortable: true,
    minWidth: 120,
    renderCell: (params): React.ReactElement => (
      <PillCell params={params} color={STATUS_PILL_COLOR[params.row.status]}>
        {translate(getStatusLabel(params.row))}
      </PillCell>
    ),
  },
  {
    field: 'dateAdded',
    headerName: translate('Date Added'),
    sortable: true,
    minWidth: 140,
    renderCell: (params): React.ReactElement => (
      <DataGridCell
        text={dateFormatString(params.row.dateAdded, 'LL')}
        params={params}
      />
    ),
  },
  {
    field: 'actions',
    // Icon-only column: a visible header just truncates to "Acti…" at this
    // width, so leave it blank (EX-39827). aria-label on the row trigger
    // carries the accessible name for the action itself.
    headerName: '',
    sortable: false,
    disableColumnMenu: true,
    width: 64,
    align: 'left',
    renderCell: (params): React.ReactElement => (
      <CardActionsMenu
        card={params.row}
        onReinvite={onReinvite}
        onUnassign={onUnassign}
        translate={translate}
      />
    ),
  },
]
